import { Button } from "@/components/ui/button";
import {
  Phone,
  ArrowRight,
  ShieldCheck,
  Clock,
  UserCheck,
  Car,
  BadgeEuro,
  MessageCircle,
  Globe,
  MapPin,
} from "lucide-react";

const HERO_IMG = "/images/hero.webp";
const HERO_IMG_FALLBACK = "/images/hero.jpg";

const DESTINATIONS = [
  "2 Mai",
  "Limanu",
  "Vama Veche",
  "Neptun",
  "Olimp",
  "Jupiter",
  "Saturn",
  "Costinești",
];

const FEATURES = [
  { icon: Clock, label: "Disponibil 24/7" },
  { icon: UserCheck, label: "Șoferi profesioniști" },
  { icon: Car, label: "Autoturisme confortabile" },
  { icon: ShieldCheck, label: "Siguranță & seriozitate" },
  { icon: BadgeEuro, label: "Prețuri corecte" },
];

export default function Hero() {
  return (
    <section
      id="acasa"
      data-testid="hero-section"
      className="relative w-full overflow-hidden grain bg-slate-950"
    >
      {/* Background image + overlays */}
      <div className="absolute inset-0">
        <picture>
          <source srcSet={HERO_IMG} type="image/webp" />
          <img
            src={HERO_IMG_FALLBACK}
            alt="GlobalTransport Mangalia — litoral Marea Neagră"
            className="w-full h-full object-cover object-center"
            width="1920"
            height="1080"
            fetchPriority="high"
            loading="eager"
            decoding="async"
          />
        </picture>
        {/* Dark diagonal + bottom-fade overlays */}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/85 to-slate-950/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-slate-950/40" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 lg:pt-32 pb-10 lg:pb-14">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          {/* LEFT — main copy */}
          <div className="lg:col-span-7 fade-up">
            <span
              data-testid="hero-eyebrow"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/15 border border-amber-400/40 text-amber-300 text-[11px] font-semibold uppercase tracking-[0.18em]"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
              Curse din Mangalia · România & Bulgaria
            </span>

            <h1
              data-testid="hero-title"
              className="mt-6 text-5xl sm:text-6xl lg:text-7xl font-black text-white leading-[0.95] tracking-tight"
            >
              GLOBAL
              <br />
              <span className="text-amber-400">TRANSPORT</span>
            </h1>

            {/* Slogan bar */}
            <div className="mt-6 inline-flex items-center gap-3 sm:gap-5 bg-slate-900/70 backdrop-blur border border-white/10 rounded-full px-5 py-2.5">
              {["Rapid", "Sigur", "La destinație"].map((w, i) => (
                <span key={w} className="flex items-center gap-3">
                  <span className="text-white text-sm sm:text-base font-semibold tracking-wide">
                    {w}
                  </span>
                  {i < 2 && <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />}
                </span>
              ))}
            </div>

            <p
              data-testid="hero-subtitle"
              className="sr-only"
            >
              GlobalTransport Mangalia — transferuri, aeroporturi, stațiuni și orașe.
            </p>

            {/* Destinations chips */}
            <div className="mt-7">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-amber-300 font-semibold">
                <MapPin className="w-3.5 h-3.5" />
                Plecări din Mangalia și împrejurimi
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {DESTINATIONS.map((d) => (
                  <span
                    key={d}
                    data-testid={`hero-destination-${d.toLowerCase().replace(/\s/g, "-")}`}
                    className="inline-flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 text-slate-100 text-sm rounded-full px-3 py-1.5 transition-colors"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                    {d}
                  </span>
                ))}
                <span className="inline-flex items-center gap-1.5 text-amber-300 text-sm font-semibold px-2 py-1.5">
                  și multe altele →
                </span>
              </div>
            </div>

            {/* Tagline */}
            <p className="mt-8 italic text-slate-200/85 text-base sm:text-lg">
              „Călătorește fără griji,{" "}
              <span className="text-white font-bold not-italic">noi te ducem la destinație!</span>
              "
            </p>
          </div>

          {/* RIGHT — Rezervă Acum card (compact) */}
          <aside
            data-testid="hero-booking-card"
            className="lg:col-span-4 lg:col-start-9 fade-up"
          >
            <div className="relative rounded-2xl bg-gradient-to-br from-amber-400 via-amber-500 to-amber-600 p-[2px] shadow-2xl amber-glow">
              <div className="rounded-[14px] bg-slate-950/95 backdrop-blur-xl px-5 py-5 sm:px-6 sm:py-6">
                <div className="flex items-center gap-3">
                  <span className="inline-flex w-9 h-9 rounded-full bg-amber-500 text-slate-950 items-center justify-center">
                    <Car className="w-5 h-5" strokeWidth={2.4} />
                  </span>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-amber-300 font-semibold">
                      Rezervă
                    </div>
                    <div className="text-2xl font-black text-white leading-none">ACUM</div>
                  </div>
                </div>

                <div className="mt-5 space-y-2.5">
                  <a
                    href="https://wa.me/40754539886"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="hero-cta-whatsapp"
                    className="group flex items-center justify-between w-full bg-emerald-500 hover:bg-emerald-400 text-white font-semibold rounded-xl px-4 py-3 transition-all"
                  >
                    <span className="flex items-center gap-2.5">
                      <MessageCircle className="w-4 h-4" strokeWidth={2.4} />
                      <span className="flex flex-col leading-tight">
                        <span className="text-[10px] uppercase tracking-widest text-white/80">
                          WhatsApp
                        </span>
                        <span className="text-base">0754 539 886</span>
                      </span>
                    </span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </a>

                  <a
                    href="tel:0742447340"
                    data-testid="hero-cta-call"
                    className="group flex items-center justify-between w-full bg-white/5 hover:bg-white/10 border border-white/10 text-white font-semibold rounded-xl px-4 py-3 transition-all"
                  >
                    <span className="flex items-center gap-2.5">
                      <Phone className="w-4 h-4 text-amber-400" strokeWidth={2.4} />
                      <span className="flex flex-col leading-tight">
                        <span className="text-[10px] uppercase tracking-widest text-slate-300">
                          Sună acum
                        </span>
                        <span className="text-base">0742 447 340</span>
                      </span>
                    </span>
                    <ArrowRight className="w-4 h-4 text-slate-300 group-hover:translate-x-1 transition-transform" />
                  </a>
                </div>

                <Button
                  asChild
                  data-testid="hero-cta-rezerva"
                  className="mt-3 w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold rounded-xl h-11 text-sm"
                >
                  <a href="#rezervare" className="flex items-center justify-center gap-2">
                    Completează formularul
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </Button>

                <div className="mt-4 pt-4 border-t border-white/10 flex items-center gap-2 text-slate-300 text-[11px]">
                  <Globe className="w-3.5 h-3.5 text-amber-400" />
                  <span className="uppercase tracking-widest">
                    www.global-trasnport.ro
                  </span>
                </div>
              </div>
            </div>
          </aside>
        </div>

        {/* BOTTOM — Feature strip */}
        <div
          data-testid="hero-features"
          className="mt-12 lg:mt-14 bg-slate-900/70 backdrop-blur-md border border-white/10 rounded-2xl px-4 sm:px-6 py-4 sm:py-5"
        >
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {FEATURES.map((f) => (
              <div
                key={f.label}
                data-testid={`hero-feature-${f.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="flex items-center gap-3 text-white"
              >
                <span className="inline-flex w-10 h-10 shrink-0 rounded-xl bg-amber-500/15 border border-amber-400/30 items-center justify-center">
                  <f.icon className="w-5 h-5 text-amber-400" strokeWidth={2} />
                </span>
                <span className="text-sm font-semibold leading-tight">{f.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
